/**
 * Offline storage - keeps cached API responses and the queue of changes made
 * offline in IndexedDB, so both survive reloads while offline.
 *
 * Everything is also kept in memory; IndexedDB is only written to. If
 * IndexedDB isn't available (e.g. some private browsing modes), offline
 * support still works for as long as the tab stays open.
 */

const DB_NAME = "flowmind-offline";
const DB_VERSION = 1;
const RESPONSES = "responses"; // GET url -> response JSON
const OUTBOX = "outbox"; // Queued changes, keyed by `seq`

let dbPromise = null;

const openDb = () => {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      if (!window.indexedDB) {
        reject(new Error("IndexedDB not available"));
        return;
      }
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(RESPONSES)) {
          db.createObjectStore(RESPONSES);
        }
        if (!db.objectStoreNames.contains(OUTBOX)) {
          db.createObjectStore(OUTBOX, { keyPath: "seq" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  return dbPromise;
};

// Run `work(store)` in a transaction; resolves when the transaction completes
const withStore = async (storeName, mode, work) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const result = work(transaction.objectStore(storeName));
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
};

const logError = (action) => (err) =>
  console.error(`Offline storage: failed to ${action}:`, err);

/**
 * Load everything that was stored
 * @returns {Promise<{responses: Map, outbox: Array}>}
 */
export const loadStoredData = async () => {
  const responses = new Map();
  const outbox = [];
  try {
    await withStore(RESPONSES, "readonly", (store) => {
      store.openCursor().onsuccess = (event) => {
        const cursor = event.target.result;
        if (!cursor) return;
        responses.set(cursor.key, cursor.value);
        cursor.continue();
      };
    });
    await withStore(OUTBOX, "readonly", (store) => {
      store.getAll().onsuccess = (event) => {
        outbox.push(...event.target.result);
      };
    });
  } catch (err) {
    logError("load")(err);
  }
  outbox.sort((a, b) => a.seq - b.seq);
  return { responses, outbox };
};

export const storeResponse = (url, data) =>
  withStore(RESPONSES, "readwrite", (store) => store.put(data, url)).catch(
    logError("store response"),
  );

export const storeOp = (op) =>
  withStore(OUTBOX, "readwrite", (store) => store.put(op)).catch(
    logError("store change"),
  );

export const removeOp = (seq) =>
  withStore(OUTBOX, "readwrite", (store) => store.delete(seq)).catch(
    logError("remove change"),
  );

export const clearStoredResponses = () =>
  withStore(RESPONSES, "readwrite", (store) => store.clear()).catch(
    logError("clear"),
  );
